// Excel export for the Reports panel.
//
// Strategy: lazy-load SheetJS from CDN only when the user actually clicks
// "Download Excel" — so we don't bloat the bundle for users who never export.
// Users who export pay a one-time ~600KB script load from cdn.sheetjs.com.
//
// Generated workbooks have multiple sheets (user picks which to include) with
// proper currency formatting, frozen header rows, and column auto-width.

// CDN fallback chain — tries each in order, uses the first that loads.
// Different users hit different network restrictions (corporate proxies, regional
// blocks, ad-blockers). Multiple sources = much higher success rate.
const SHEETJS_CDN_URLS = [
  'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js',
  'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js',
  'https://unpkg.com/xlsx@0.18.5/dist/xlsx.full.min.js',
];

let xlsxLoadPromise = null;

/** Try to load a script from one URL. Resolves with window.XLSX on success. */
function tryLoadScript(url, globalName = 'XLSX') {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.crossOrigin = 'anonymous';
    const cleanup = () => {
      script.onload = null;
      script.onerror = null;
    };
    const timeout = setTimeout(() => {
      cleanup();
      script.remove();
      reject(new Error(`Timeout loading ${url}`));
    }, 12000);
    script.onload = () => {
      clearTimeout(timeout);
      cleanup();
      if (window[globalName]) resolve(window[globalName]);
      else reject(new Error(`Loaded ${url} but global ${globalName} not found`));
    };
    script.onerror = () => {
      clearTimeout(timeout);
      cleanup();
      script.remove();
      reject(new Error(`Failed to load ${url}`));
    };
    document.head.appendChild(script);
  });
}

/** Lazy-load SheetJS, trying multiple CDNs in order. */
function loadSheetJs() {
  if (typeof window !== 'undefined' && window.XLSX) return Promise.resolve(window.XLSX);
  if (xlsxLoadPromise) return xlsxLoadPromise;

  xlsxLoadPromise = (async () => {
    let lastErr = null;
    for (const url of SHEETJS_CDN_URLS) {
      try {
        const xlsx = await tryLoadScript(url, 'XLSX');
        return xlsx;
      } catch (err) {
        lastErr = err;
        // Try next CDN
      }
    }
    xlsxLoadPromise = null;  // allow retry on next user click
    throw new Error(
      'Could not load Excel export library from any CDN. ' +
      'Check your network or ad-blocker. ' +
      `(Last error: ${lastErr ? lastErr.message : 'unknown'})`
    );
  })();
  return xlsxLoadPromise;
}

/** Format a date for spreadsheet display. */
function fmtDate(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtMonth(yyyymm) {
  const [y, m] = yyyymm.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

const MON_LONG = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];

/** Format a bucket key for spreadsheet display. Verbose / business-friendly. */
function fmtBucket(key, granularity) {
  if (!key || typeof key !== 'string') return key || '';
  if (granularity === 'day') {
    // "YYYY-MM-DD" → "Apr 12, 2026"
    const [y, m, d] = key.split('-');
    return `${MON_LONG[parseInt(m, 10) - 1].slice(0, 3)} ${parseInt(d, 10)}, ${y}`;
  }
  if (granularity === 'week') {
    // Engine emits the Monday-of-week as "YYYY-MM-DD"
    const [y, m, d] = key.split('-');
    return `Week of ${MON_LONG[parseInt(m, 10) - 1].slice(0, 3)} ${parseInt(d, 10)}, ${y}`;
  }
  if (granularity === 'quarter') {
    // "YYYY-Qn"
    const [y, q] = key.split('-');
    return `${q} ${y}`;
  }
  // month — "YYYY-MM"
  return fmtMonth(key);
}

const SHEET_NAMES = {
  day:     'Daily Trend',
  week:    'Weekly Trend',
  month:   'Monthly Trend',
  quarter: 'Quarterly Trend',
};

const PERIOD_HEADERS = {
  day:     'Day',
  week:    'Week',
  month:   'Month',
  quarter: 'Quarter',
};

const DOW_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Apply currency cell format to a column range. */
function applyCurrencyFormat(ws, columns, currencyCode = 'USD') {
  const sym = { USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'AU$' }[currencyCode] || '$';
  const fmt = `"${sym}"#,##0.00`;
  if (!ws['!ref']) return;
  const range = window.XLSX.utils.decode_range(ws['!ref']);
  for (const colIdx of columns) {
    for (let r = range.s.r + 1; r <= range.e.r; r++) {
      const cellRef = window.XLSX.utils.encode_cell({ r, c: colIdx });
      if (ws[cellRef] && typeof ws[cellRef].v === 'number') {
        ws[cellRef].z = fmt;
      }
    }
  }
}

/** Set column widths based on content. */
function autoSizeColumns(ws, rows) {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  ws['!cols'] = headers.map(h => {
    let max = h.length;
    for (const row of rows) {
      const v = row[h];
      if (v != null) {
        const len = String(v).length;
        if (len > max) max = len;
      }
    }
    return { wch: Math.min(Math.max(max + 2, 10), 50) };
  });
}

/**
 * Build and download an Excel workbook from a report.
 *
 * @param {object} report - Output of buildReport() or aggregateFromSales()
 * @param {object} options - { includeSummary, includeMonthly, includeProducts, includeAllSales, includeInsights }
 * @param {string} fileName - Without .xlsx extension
 * @param {object} filterMeta - Optional { rangeLabel, rangeStart, rangeEnd, granularity } describing
 *                              the active filter so it gets documented in the Summary sheet.
 */
export async function exportReportToExcel(report, options = {}, fileName = 'EtsyReport', filterMeta = null) {
  const XLSX = await loadSheetJs();

  const opts = {
    includeSummary:   options.includeSummary !== false,
    includeMonthly:   options.includeMonthly !== false,
    includeProducts:  options.includeProducts !== false,
    includeAllSales:  options.includeAllSales !== false,
    includeInsights:  options.includeInsights !== false,
  };

  const { summary, byMonth, topProducts, insights, sales = [] } = report;
  const currency = summary?.currency || 'USD';
  const wb = XLSX.utils.book_new();
  let sheetCount = 0;

  // ─── SHEET 1: Summary ───────────────────────────────────────────
  if (opts.includeSummary && summary) {
    const sumRows = [];
    // Filter context block (only if a non-default filter is active)
    const hasDateFilter = filterMeta && filterMeta.rangeLabel && filterMeta.rangeLabel !== 'All time';
    const hasDayFilter = filterMeta && Array.isArray(filterMeta.daysOfWeek)
      && filterMeta.daysOfWeek.length > 0 && filterMeta.daysOfWeek.length < 7;
    if (hasDateFilter || hasDayFilter) {
      if (hasDateFilter) {
        sumRows.push({ Metric: 'Filter: date range',  Value: filterMeta.rangeLabel });
        sumRows.push({ Metric: 'Filter: start',       Value: fmtDate(filterMeta.rangeStart) });
        sumRows.push({ Metric: 'Filter: end',         Value: fmtDate(filterMeta.rangeEnd) });
      }
      if (hasDayFilter) {
        const days = filterMeta.daysOfWeek.slice().sort().map(i => DOW_NAMES[i]).join(', ');
        sumRows.push({ Metric: 'Filter: days of week', Value: days });
      }
      if (filterMeta.granularity) {
        sumRows.push({ Metric: 'Trend granularity',   Value: filterMeta.granularity });
      }
      sumRows.push({ Metric: '', Value: '' });
    }
    sumRows.push(
      { Metric: 'Date range start', Value: fmtDate(summary.dateStart) },
      { Metric: 'Date range end',   Value: fmtDate(summary.dateEnd) },
      { Metric: 'Currency',         Value: currency },
      { Metric: '',                 Value: '' },
      { Metric: 'Total orders',     Value: summary.validSaleCount || 0 },
      { Metric: 'Refunded orders',  Value: summary.refundCount || 0 },
      { Metric: 'Refund rate',      Value: ((summary.refundRate || 0) * 100).toFixed(2) + '%' },
      { Metric: 'Items sold',       Value: summary.totalQuantity || 0 },
      { Metric: 'Unique buyers',    Value: summary.uniqueBuyers || 0 },
      { Metric: '',                 Value: '' },
      { Metric: 'Total revenue',         Value: round(summary.totalRevenue) },
      { Metric: 'Total Etsy fees',       Value: round(summary.totalFees) },
      { Metric: 'Effective fee rate',    Value: (summary.effectiveFeeRate || 0).toFixed(2) + '%' },
      { Metric: 'Net profit (excl. costs)', Value: round(summary.totalProfit) },
      { Metric: 'Profit margin',         Value: (summary.profitMargin || 0).toFixed(2) + '%' },
      { Metric: 'Avg order value',       Value: round(summary.avgOrderValue) },
      { Metric: 'Total discounts given', Value: round(summary.totalDiscounts) },
      { Metric: '',                 Value: '' },
      { Metric: 'Fees source',     Value: summary.feesAreEstimated ? 'Estimated from calculator settings' : 'Calculated from Order Net column' },
    );
    const ws = XLSX.utils.json_to_sheet(sumRows);
    autoSizeColumns(ws, sumRows);
    applyCurrencyFormat(ws, [1], currency);
    ws['!freeze'] = { ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, 'Summary');
    sheetCount++;
  }

  // ─── SHEET 2: Trend breakdown (granularity-aware) ───────────────
  // Prefer the active byBucket (whatever granularity user picked); fall back
  // to byMonth for older call sites that don't populate byBucket.
  const trendData = (report.byBucket && report.byBucket.length > 0) ? report.byBucket : byMonth;
  const trendGranularity = filterMeta?.granularity || 'month';
  if (opts.includeMonthly && trendData?.length > 0) {
    const periodHeader = PERIOD_HEADERS[trendGranularity] || 'Period';
    const sheetName = SHEET_NAMES[trendGranularity] || 'Trend';
    const trendRows = trendData.map(m => ({
      [periodHeader]: fmtBucket(m.bucket || m.month, trendGranularity),
      'Order Count': m.orderCount,
      'Revenue': round(m.revenue),
      'Etsy Fees': round(m.fees),
      'Profit': round(m.profit),
      'Avg per Order': m.orderCount > 0 ? round(m.revenue / m.orderCount) : 0,
    }));
    const ws = XLSX.utils.json_to_sheet(trendRows);
    autoSizeColumns(ws, trendRows);
    applyCurrencyFormat(ws, [2, 3, 4, 5], currency);
    ws['!freeze'] = { ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    sheetCount++;
  }

  // ─── SHEET 3: Top products ──────────────────────────────────────
  if (opts.includeProducts && topProducts?.length > 0) {
    const totalRev = summary?.totalRevenue || 1;
    const productRows = topProducts.map((p, i) => ({
      'Rank': i + 1,
      'Product Name': p.name,
      'Quantity Sold': p.quantity,
      'Order Count': p.orderCount,
      'Revenue': round(p.revenue),
      '% of Revenue': ((p.revenue / totalRev) * 100).toFixed(2) + '%',
      'Etsy Fees': round(p.fees),
      'Profit': round(p.profit),
      'Refund Count': p.refundCount,
      'Avg Sale Value': p.orderCount > 0 ? round(p.revenue / p.orderCount) : 0,
    }));
    const ws = XLSX.utils.json_to_sheet(productRows);
    autoSizeColumns(ws, productRows);
    applyCurrencyFormat(ws, [4, 6, 7, 9], currency);
    ws['!freeze'] = { ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, 'Top Products');
    sheetCount++;
  }

  // ─── SHEET 4: All sales (raw transaction data) ──────────────────
  if (opts.includeAllSales && sales?.length > 0) {
    const saleRows = sales.map(s => ({
      'Date': fmtDate(s.date) || s.dateStr,
      'Day of Week': s.date ? DOW_NAMES[s.date.getDay()] : '',
      'Order ID': s.orderId,
      'Item Name': s.itemName,
      'Status': s.isRefund ? 'Refunded' : 'Sold',
      'Quantity': s.quantity,
      'Item Total': round(s.itemTotal),
      'Shipping': round(s.shipping),
      'Sales Tax': round(s.salesTax),
      'Discount': round(s.discount),
      'Estimated Fees': round(s.totalFees || 0),
      'Estimated Profit': round(s.estimatedProfit || 0),
      'Order Net (if provided)': s.orderNet !== null ? round(s.orderNet) : '',
      'Buyer ID': s.buyerId || '',
      'Currency': s.currency || currency,
    }));
    const ws = XLSX.utils.json_to_sheet(saleRows);
    autoSizeColumns(ws, saleRows);
    // Currency columns shifted by +1 because of new "Day of Week" column at index 1
    applyCurrencyFormat(ws, [6, 7, 8, 9, 10, 11, 12], currency);
    ws['!freeze'] = { ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, 'All Sales');
    sheetCount++;
  }

  // ─── SHEET 5: Insights (text) ───────────────────────────────────
  if (opts.includeInsights && insights?.length > 0) {
    const insightRows = insights.map((ins, i) => ({
      '#': i + 1,
      'Type': ins.type || '',
      'Insight': `${ins.icon || ''} ${ins.text}`.trim(),
    }));
    const ws = XLSX.utils.json_to_sheet(insightRows);
    autoSizeColumns(ws, insightRows);
    ws['!freeze'] = { ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, 'Insights');
    sheetCount++;
  }

  if (sheetCount === 0) {
    throw new Error('Pick at least one section to include in the export.');
  }

  // Download
  const today = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${fileName}_${today}.xlsx`);
  return { sheetCount };
}

function round(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/**
 * Capture the report DOM element as a PNG and trigger download.
 * Uses html2canvas, lazy-loaded from CDN.
 */
export async function exportReportAsImage(elementId, fileName = 'EtsyReport') {
  // Lazy-load html2canvas with CDN fallback chain
  if (!window.html2canvas) {
    const H2C_URLS = [
      'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js',
      'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js',
      'https://unpkg.com/html2canvas@1.4.1/dist/html2canvas.min.js',
    ];
    let h2cErr = null;
    for (const url of H2C_URLS) {
      try {
        await tryLoadScript(url, 'html2canvas');
        h2cErr = null;
        break;
      } catch (err) {
        h2cErr = err;
      }
    }
    if (h2cErr) {
      throw new Error('Could not load image export library from any CDN. Check your network or ad-blocker.');
    }
  }
  const el = document.getElementById(elementId);
  if (!el) throw new Error('Report element not found.');

  const canvas = await window.html2canvas(el, {
    backgroundColor: '#fefdf8',
    scale: 2, // crisp on retina
    logging: false,
    useCORS: true,
  });

  const today = new Date().toISOString().slice(0, 10);
  const link = document.createElement('a');
  link.download = `${fileName}_${today}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
  return { width: canvas.width, height: canvas.height };
}
